import { components } from "@/api-schema";
import { CompanyType, RelocationOrderStatus } from "@/api-schema-enums";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { isRelocationOrderTerminalStatus } from "@/common/helpers/relocation-order-terminal-status";
import { AttributeAppRolePrefix, extractAttributeAppRolesByPrefix } from "@/common/helpers/user-attribute-app-roles";
import { useRelocationOrderExternalActions } from "@/common/hooks/relocation-order/use-relocation-order-external-actions";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { RelocationOrderTable } from "@/components/data-table/predefined/relocation-order-table";
import { relocationTableOrderDefaultColumns } from "@/components/data-table/predefined/relocation-order-table-columns";
import type { Filter } from "@/components/filter/_types";
import { OrderedByMeFilter } from "@/components/filter/predefined/ordered-by-me-filter";
import {
  RELOCATION_ORDER_STATUS_FILTER_OPTIONS,
  RelocationOrderStatusFilter,
} from "@/components/filter/predefined/relocation-order-status-filter";
import { RelocationOrderTransportReasonFilter } from "@/components/filter/predefined/relocation-order-transport-reason-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { CommandButton } from "@/components/command-palette/command-button";
import { ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { userStore } from "@/store/user-store";
import { TruckIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TransportCompanyAssignmentFallback } from "../_components/transport-company-assignment-fallback";
import { useTransportCompanyAssignment } from "../_hooks/use-transport-company-assignment";

type RelocationOrderSearchBody = components["schemas"]["RelocationOrderSearchBody"];

const RELOCATION_REQUEST_COLUMNS = [
  relocationTableOrderDefaultColumns.mobile.columnMobileComplete,
  relocationTableOrderDefaultColumns.tablet.columnCompactVehicleInfo,
  relocationTableOrderDefaultColumns.desktop.columnVehicleInfoWithBrand,
  relocationTableOrderDefaultColumns.widescreen.columnBrand,
  relocationTableOrderDefaultColumns.widescreen.columnModelGenerationName,
  relocationTableOrderDefaultColumns.widescreen.columnVin,
  relocationTableOrderDefaultColumns.widescreen.columnColor,
  relocationTableOrderDefaultColumns.tablet.columnExternalCompactRoute,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnExternalRouteWithStatus,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnEarliestPickupDate,
];

export function RelocationRequestView() {
  const { t } = useTranslation(["fit", "common"]);
  const pageTitle = t("fit:TransportRequests");
  const pageSubtitle = t("fit:TransportRequestsDescription");
  const { shouldShowFallback } = useTransportCompanyAssignment();

  const { user } = userStore();
  const userCompanyAttributeAppRoles = extractAttributeAppRolesByPrefix(user?.roles, AttributeAppRolePrefix.Company);
  const { data: userCompanies, isLoading: isLoadingUserCompanies } = useCompanySearchQuery({
    options: {
      body: {
        technicalId: userCompanyAttributeAppRoles,
      },
      parameters: {
        pageSize: UNLIMITED_PAGE_SIZE,
      },
    },
  });
  const { customRowActions } = useRelocationOrderExternalActions();

  const defaultStatuses = useMemo(() => {
    return Object.values(RELOCATION_ORDER_STATUS_FILTER_OPTIONS)
      .filter((status) => !isRelocationOrderTerminalStatus(status.id))
      .map((status) => status.id);
  }, []);

  const [filterValues, setFilterValues] = useState({
    orderedBy: [],
    status: defaultStatuses,
    transportReason: [],
  });

  const userHasTransportCompanyAssigned = useMemo(() => {
    return userCompanies?.data?.some((company) => company.type === CompanyType.Transport) ?? false;
  }, [userCompanies]);

  const filters = useMemo(() => {
    const nextFilters: Filter<unknown, unknown>[] = [];

    if (user.uuid) {
      nextFilters.push(new OrderedByMeFilter(user.uuid));
    }

    nextFilters.push(new RelocationOrderStatusFilter(), new RelocationOrderTransportReasonFilter());

    return nextFilters;
  }, [user.uuid]);

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: values,
    }));
  }, []);

  const relocationRequestBody: RelocationOrderSearchBody = useMemo(() => {
    const transportCompanies =
      userCompanies?.data
        ?.filter((company) => company.type === CompanyType.Transport)
        .map((company) => company.uuid)
        .filter(Boolean) || [];

    return {
      transportCompany: transportCompanies,
      ...(filterValues.status.length > 0 && {
        status: filterValues.status as RelocationOrderStatus[],
      }),
      ...(filterValues.transportReason.length > 0 && {
        transportReason: filterValues.transportReason,
      }),
    };
  }, [userCompanies, filterValues.status, filterValues.transportReason]);

  const isTodoFilterActive = useMemo(() => {
    if (!filterValues) return false;
    return (
      filterValues.status.length === 2 &&
      filterValues.status.includes(RelocationOrderStatus.BranchAwaitingPickup) &&
      filterValues.status.includes(RelocationOrderStatus.TransportCompanyAwaitingPickupDate)
    );
  }, [filterValues]);

  const switchTodoFilterActive = useCallback(
    (active: boolean) => {
      const statusesToFilter: RelocationOrderStatus[] = active
        ? [RelocationOrderStatus.BranchAwaitingPickup, RelocationOrderStatus.TransportCompanyAwaitingPickupDate]
        : defaultStatuses;

      handleFilterChange("status", statusesToFilter);
    },
    [handleFilterChange, defaultStatuses],
  );

  const header = (
    <CommandButton
      commandId="fit-relocation-request-my-todo"
      label={t("fit:MyToDo")}
      className="w-full"
      variant={isTodoFilterActive ? ButtonVariant.Filled : ButtonVariant.Outlined}
      colorVariant={isTodoFilterActive ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
      onClick={() => switchTodoFilterActive(!isTodoFilterActive)}
    />
  );

  const content = isLoadingUserCompanies ? (
    <div className="space-y-6">
      <Skeleton className="h-14 w-lg" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-200 w-full" />
    </div>
  ) : shouldShowFallback || !userHasTransportCompanyAssigned ? (
    <TransportCompanyAssignmentFallback />
  ) : (
    <RelocationOrderTable
      columns={RELOCATION_REQUEST_COLUMNS}
      body={relocationRequestBody}
      filterContainerCustomContent={header}
      filters={filters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      defaultRowActions={{ showView: true }}
      customRowActions={customRowActions}
      onRowNavigate={createRowNavigatePath("/fit/relocation-request")}
      noDataIcon={TruckIcon}
      noDataTitle={t("fit:NoTransportRequestsFound")}
      noDataDescription={t("fit:NoTransportRequestsFoundDescription")}
      noResultsTitle={t("fit:NoTransportRequestsMatchFilters")}
      noResultsDescription={t("fit:NoTransportRequestsMatchFiltersDescription")}
      showExportButton
    />
  );

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Table} title={pageTitle} subTitle={pageSubtitle}>
      {content}
    </CenteredLayout>
  );
}
